import { Household } from '@/models/Household';
import { HomeHQAccess } from '@/models/Subscription';

export type PaywallAction = 'choose_plan' | 'renew' | 'contact_owner';

export interface PaywallCopy {
  title: string;
  body: string;
  action: PaywallAction;
  /** Primary button label. */
  cta: string;
  /** For contact_owner: who to reach out to. */
  owner?: { name: string; email: string };
}

const SUBSCRIBE_TO_CONTINUE = 'Please subscribe to continue using Hauser.';

/** What the HomeHQ paywall says and offers, for each reason access is off. */
export const getPaywallCopy = (
  access: HomeHQAccess,
  household: Household | undefined,
): PaywallCopy => {
  // Household member whose owner's plan lapsed: only the owner can fix it.
  if (access.householdOwnerId) {
    const owner = household?.owner;
    const ownerName = owner?.name || owner?.email || 'The account owner';
    return {
      title: "Your household's subscription has ended",
      body: `${ownerName} needs to renew the subscription to continue using Hauser. Reach out to them to restore access for your household.`,
      action: 'contact_owner',
      cta: `Contact ${owner?.name || 'the account owner'}`,
      owner: owner ? { name: ownerName, email: owner.email } : undefined,
    };
  }

  switch (access.status) {
    // Promo/trial ended with no card, or a renewal charge failed: the
    // subscription still exists, so renewing = adding/updating the card.
    case 'past_due':
      return access.hasPaymentMethod
        ? {
            title: "We couldn't renew your subscription",
            body: 'Please update your payment details to continue using Hauser.',
            action: 'renew',
            cta: 'Renew subscription now',
          }
        : {
            title: 'Your trial period has ended',
            body: SUBSCRIBE_TO_CONTINUE,
            action: 'renew',
            cta: 'Renew subscription now',
          };
    case 'expired':
      return {
        title: 'Your trial period has ended',
        body: SUBSCRIBE_TO_CONTINUE,
        action: 'choose_plan',
        cta: 'Renew subscription now',
      };
    case 'canceled':
      return {
        title: 'Your subscription has ended',
        body: SUBSCRIBE_TO_CONTINUE,
        action: 'choose_plan',
        cta: 'Renew subscription now',
      };
    default:
      return {
        title: 'Welcome to Home HQ',
        body: 'Choose a plan to start tracking your home, inspection reports, and repairs in one place.',
        action: 'choose_plan',
        cta: 'Subscribe now',
      };
  }
};

export const formatPrice = (cents: number, currency: string) =>
  new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
  }).format(cents / 100);
